import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button, Empty, Typography, theme } from 'antd';
import { CloseOutlined, ThunderboltOutlined, RobotOutlined } from '@ant-design/icons';
import { useDismissibleLayer } from '../hooks/useDismiss';
import type { CommandDefinition, RemoteCommand, CommandGroup } from '../commands/registry';

const { Text } = Typography;

interface Props {
  groups: CommandGroup[];
  visible: boolean;
  selectedIndex: number;
  onSelect: (cmd: CommandDefinition | RemoteCommand) => void;
  onClose: () => void;
}

export function isRemoteCommand(cmd: CommandDefinition | RemoteCommand): cmd is RemoteCommand {
  return 'agentId' in cmd;
}

function collectRemotesByName(groups: CommandGroup[], name: string): RemoteCommand[] {
  const result: RemoteCommand[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (isRemoteCommand(item) && item.name === name) {
        result.push(item);
      }
    }
  }
  return result;
}

function preventMouseDownDefault(event: React.MouseEvent<HTMLElement>) {
  event.preventDefault();
}

export default function SlashCommandPicker({ groups, visible, selectedIndex, onSelect, onClose }: Props) {
  const { token } = theme.useToken();
  const [agentSelecting, setAgentSelecting] = useState<{ commandName: string; agents: RemoteCommand[] } | null>(null);
  const [selectedAgentIndex, setSelectedAgentIndex] = useState(0);
  const activeItemRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, selectedAgentIndex]);

  useDismissibleLayer({
    active: visible,
    within: containerRef,
    onEscape: onClose,
    onOutsidePress: onClose,
  });

  const handleItemClick = useCallback((cmd: CommandDefinition | RemoteCommand) => {
    if (isRemoteCommand(cmd)) {
      const matches = collectRemotesByName(groups, cmd.name);
      if (matches.length > 1) {
        setAgentSelecting({ commandName: cmd.name, agents: matches });
        setSelectedAgentIndex(0);
        return;
      }
    }
    onSelect(cmd);
  }, [groups, onSelect]);

  const handleAgentSelect = useCallback((cmd: RemoteCommand) => {
    setAgentSelecting(null);
    onSelect(cmd);
  }, [onSelect]);

  const handleBackToMain = useCallback(() => {
    setAgentSelecting(null);
    setSelectedAgentIndex(0);
  }, []);

  if (!visible) return null;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '100%',
    left: 16,
    right: 16,
    maxHeight: 240,
    overflowY: 'auto',
    overflowX: 'hidden',
    background: token.colorBgElevated,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius * 2,
    boxShadow: token.boxShadowSecondary,
    zIndex: 50,
    marginBottom: 4,
    padding: '4px 0',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px 4px',
  };

  const itemStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: 'calc(100% - 8px)',
    padding: '8px 12px',
    border: 'none',
    background: active ? token.colorFillSecondary : 'transparent',
    color: token.colorText,
    fontSize: 14,
    textAlign: 'left',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    margin: '0 4px',
    boxSizing: 'border-box',
    transition: 'background 0.15s',
  });

  if (agentSelecting) {
    return (
      <div ref={containerRef} className="slash-command-picker" style={containerStyle}>
        <div style={headerStyle}>
          <Text strong style={{ fontSize: 13 }}>
            <ThunderboltOutlined style={{ marginRight: 6, color: token.colorPrimary }} />
            /{agentSelecting.commandName} — Choose Agent
          </Text>
          <Button
            type="text"
            size="small"
            aria-label="Back" icon={<CloseOutlined />}
            onMouseDown={(event: React.MouseEvent<HTMLElement>) => { preventMouseDownDefault(event); handleBackToMain(); }}
          />
        </div>
        <Text type="secondary" style={{ display: 'block', padding: '2px 12px 6px', fontSize: 12 }}>
          Multiple agents provide this command:
        </Text>
        {agentSelecting.agents.map((agent, idx) => (
          <button
            key={agent.agentId}
            ref={idx === selectedAgentIndex ? activeItemRef : undefined}
            style={{
              ...itemStyle(idx === selectedAgentIndex),
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 2,
              width: 'calc(100% - 8px)',
            }}
            onMouseDown={(event: React.MouseEvent<HTMLElement>) => { preventMouseDownDefault(event); handleAgentSelect(agent); }}
          >
            <Text strong style={{ fontSize: 13 }}>
              <RobotOutlined style={{ marginRight: 6, color: token.colorPrimary }} />
              {agent.agentName}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, paddingLeft: 20 }}>
              {agent.description}
            </Text>
          </button>
        ))}
      </div>
    );
  }

  const totalCount = groups.reduce((sum, g) => sum + g.items.length, 0);

  if (totalCount === 0) {
    return (
      <div ref={containerRef} className="slash-command-picker" style={containerStyle}>
        <div style={headerStyle}>
          <Text strong style={{ fontSize: 13 }}>
            <ThunderboltOutlined style={{ marginRight: 6, color: token.colorPrimary }} />
            Slash Commands
          </Text>
          <Button
            type="text"
            size="small"
            aria-label="Close" icon={<CloseOutlined />}
            onMouseDown={(event: React.MouseEvent<HTMLElement>) => { preventMouseDownDefault(event); onClose(); }}
          />
        </div>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="没有找到命令"
          style={{ margin: '8px 0' }}
        />
      </div>
    );
  }

  let flatIndex = 0;

  return (
    <div ref={containerRef} className="slash-command-picker" style={containerStyle}>
      <div style={headerStyle}>
        <Text strong style={{ fontSize: 13 }}>
          <ThunderboltOutlined style={{ marginRight: 6, color: token.colorPrimary }} />
          Slash Commands
        </Text>
        <Button
          type="text"
          size="small"
          aria-label="Close" icon={<CloseOutlined />}
          onMouseDown={(event: React.MouseEvent<HTMLElement>) => { preventMouseDownDefault(event); onClose(); }}
        />
      </div>
      {groups.map((group) => (
        <React.Fragment key={group.group}>
          <div style={{ padding: '6px 12px 2px' }}>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {group.group === 'System' ? 'System' : `🤖 ${group.group}`}
            </Text>
          </div>
          {group.items.map((cmd) => {
            const currentIndex = flatIndex++;
            const isActive = currentIndex === selectedIndex;
            return (
              <button
                key={`${group.group}-${cmd.name}`}
                ref={isActive ? activeItemRef : undefined}
                style={itemStyle(isActive)}
                onMouseDown={(e) => { e.preventDefault(); handleItemClick(cmd); }}
              >
                <Text
                  strong
                  style={{
                    color: token.colorPrimary,
                    fontSize: 13,
                    minWidth: 80,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  /{cmd.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {cmd.description}
                </Text>
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
